/**
 * Frontend-only tag category mapping.
 * Groups existing tag slugs into higher-level categories for cleaner UI display.
 * No DB or API changes needed — this is purely a presentation layer concern.
 *
 * Tags not listed here will be placed in an "Other" category.
 */

interface TagCategory {
  label: string;
  description: string;
  slugs: string[];
}

const TAG_CATEGORIES: TagCategory[] = [
  {
    label: "Market Data",
    description: "Price feeds, charts, and market indicators",
    slugs: [
      "price",
      "volume",
      "ohlc",
      "chart",
      "indicators",
      "indicator",
      "market-cap",
      "sentiment",
      "trending",
      "tickers",
      "historical",
      "history",
      "market",
      "spot",
    ],
  },
  {
    label: "DeFi & Trading",
    description: "Decentralized finance, swaps, and trading protocols",
    slugs: [
      "defi",
      "onchain",
      "evm",
      "blockchain",
      "token",
      "erc-20",
      "swap",
      "amm",
      "lp",
      "pool",
      "dex",
      "futures",
      "derivatives",
      "perpetual",
      "trading",
      "liquidity",
      "yield",
      "lending",
      "nft",
      "wallet",
      "vesting",
      "rewards",
      "claim",
      "distribution",
      "taker",
      "withdraw",
      "positions",
      "exchange",
      "exchanges",
    ],
  },
  {
    label: "Portfolio & Analytics",
    description: "Balance tracking, analysis, and portfolio management",
    slugs: [
      "balance",
      "portfolio",
      "valuation",
      "etf",
      "whales",
      "whale",
      "macro",
      "analysis",
      "institutional",
      "finance",
      "accounting",
      "bookkeeping",
    ],
  },
  {
    label: "Data & Actions",
    description: "Read, write, and query operations",
    slugs: [
      "read",
      "write",
      "create",
      "update",
      "list",
      "search",
      "query",
      "info",
      "metadata",
      "data",
      "utility",
      "storage",
      "database",
      "notification",
      "alert",
      "email",
      "messaging",
    ],
  },
  {
    label: "Infrastructure",
    description: "APIs, connectors, and platform integrations",
    slugs: [
      "integration",
      "oauth",
      "webhook",
      "api",
      "native",
      "multi-chain",
      "contract",
      "payment",
      "spreadsheet",
      "social",
    ],
  },
  {
    label: "Apps",
    description: "Specific platform and protocol integrations",
    slugs: [
      "coingecko",
      "pipedream",
      "uniswap",
      "polymarket",
      "railgun",
      "morpho",
      "sim-dune",
      "base",
      "zerion",
      "2chat",
      "google-sheets",
      "quickbooks",
      "shopify-oauth",
      "bitcoin",
      "btc",
      "eth",
      "cryptocurrency",
      "privacy",
    ],
  },
];

/**
 * Reverse lookup: tag slug -> category label.
 * Built eagerly at module load time for O(1) lookups.
 */
const _slugToCategoryCache: Map<string, string> = (() => {
  const cache = new Map<string, string>();
  for (const cat of TAG_CATEGORIES) {
    for (const slug of cat.slugs) {
      cache.set(slug.toLowerCase(), cat.label);
    }
  }
  return cache;
})();

/**
 * Ordered list of category labels (for sidebar display order).
 */
export const TAG_CATEGORY_LABELS = TAG_CATEGORIES.map(c => c.label);

/**
 * Map of category label -> TagCategory definition for O(1) access.
 * Use this instead of TAG_CATEGORIES.find() in render code.
 */
export const TAG_CATEGORY_MAP = new Map(TAG_CATEGORIES.map(c => [c.label, c]));

/**
 * Get the category label for a given tag slug.
 * Returns "Other" if the tag isn't mapped to any category.
 */
export function getCategoryForTag(slug: string): string {
  return _slugToCategoryCache.get(slug.toLowerCase()) ?? "Other";
}

/**
 * Group an array of tags by their category.
 * Returns entries sorted by the TAG_CATEGORIES order, with "Other" last.
 */
export function groupTagsByCategory<T extends { slug: string }>(tags: T[]): { category: string; tags: T[] }[] {
  const groups = new Map<string, T[]>();

  // Initialize groups in order
  for (const cat of TAG_CATEGORIES) {
    groups.set(cat.label, []);
  }
  groups.set("Other", []);

  // Assign tags to groups
  // getCategoryForTag always returns a known label or "Other", both of which
  // are initialised above, so the lookup is always defined.
  for (const tag of tags) {
    const category = getCategoryForTag(tag.slug);
    groups.get(category)!.push(tag);
  }

  // Filter out empty groups and return
  return Array.from(groups.entries())
    .filter(([, tagList]) => tagList.length > 0)
    .map(([category, tagList]) => ({ category, tags: tagList }));
}
